import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import CustomButton from "../CustomButton";
import axiosInstance from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { ParseErrors } from "../../utils/ParseErrors";
import { AuthContext } from "../../providers/AuthProvider";
import clsx from "clsx";

interface TOtpForm {
  password: string;
  onBack: () => void;
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  if (!user || !domain) return email;
  if (user.length <= 2) return email;
  return `${user[0]}${"•".repeat(Math.min(user.length - 2, 5))}${user[user.length - 1]}@${domain}`;
};

const OtpForm = ({ password, onBack }: TOtpForm) => {
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState<string[]>(
    Array(OTP_LENGTH).fill(""),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const otp = digits.join("");

  useEffect(() => {
    const fetchOtpEmail = async () => {
      try {
        const res = await axiosInstance.get("/auth/otp-email");
        setEmail(res.data.email);
      } catch (error) {
        toast.error(ParseErrors(error));
        onBack();
      }
    };

    fetchOtpEmail();
    inputRefs.current[0]?.focus();
  }, [onBack]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const updateDigits = (next: string[]) => {
    setDigits(next);
  };

  const handleDigitChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = value;
    updateDigits(next);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && otp.length === OTP_LENGTH) {
      handleVerifyOtp();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pasted) return;

    const next = pasted
      .split("")
      .concat(Array(OTP_LENGTH).fill(""))
      .slice(0, OTP_LENGTH);
    updateDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error("Please enter the full 6-digit code");
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.post("/auth/verify-otp", { otp });
      const session = await axiosInstance.get("/auth/login");
      auth?.UpdateLoginData(session.data);
      toast.success("Logged in successfully");
      navigate("/");
    } catch (error) {
      toast.error(ParseErrors(error));
      updateDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    try {
      await axiosInstance.post("/auth/login", { email, password });
      updateDigits(Array(OTP_LENGTH).fill(""));
      setResendTimer(RESEND_COOLDOWN);
      inputRefs.current[0]?.focus();
      toast.success("A new code has been sent");
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto rounded-2xl border border-[#E4EBF5] bg-white px-5 py-6 shadow-[0_20px_50px_rgba(21,44,85,0.08)] [backface-visibility:hidden] sm:px-10 sm:py-10">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 font-[Gilroy-Medium] text-[13px] text-[#6B7C99] transition-colors hover:text-[#152C55]"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="text-[11px]" />
        Back to sign in
      </button>

      <div className="mb-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1562EE] to-[#4F8EF7] shadow-[0_10px_24px_rgba(21,98,238,0.25)]">
          <FontAwesomeIcon
            icon={faShieldHalved}
            className="text-[22px] text-white"
          />
        </div>
        <p className="mb-1 font-[Gilroy-Medium] text-[11px] uppercase tracking-[0.14em] text-[#6B7C99]">
          Two-step verification
        </p>
        <h2 className="font-[Gilroy-Bold] text-[26px] text-[#152C55]">
          Enter verification code
        </h2>
        <p className="mx-auto mt-3 max-w-[280px] font-[Gilroy-Medium] text-[14px] leading-relaxed text-[#6B7C99]">
          We sent a 6-digit code to{" "}
          <span className="font-[Gilroy-Bold] text-[#152C55]">
            {maskEmail(email)}
          </span>
        </p>
      </div>

      <div className="space-y-6">
        <div className="flex justify-center gap-1.5 sm:gap-3">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={clsx(
                "h-11 min-w-0 max-w-[48px] flex-1 rounded-xl border bg-[#F7F9FC] text-center font-[Gilroy-Bold] text-[20px] text-[#152C55] outline-none transition-all sm:h-[56px] sm:w-[48px] sm:flex-none sm:text-[22px]",
                digit
                  ? "border-[#1562EE] bg-white shadow-[0_0_0_3px_rgba(21,98,238,0.1)]"
                  : "border-[#E4EBF5] focus:border-[#1562EE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(21,98,238,0.1)]",
              )}
            />
          ))}
        </div>

        <p className="text-center font-[Gilroy-Medium] text-[12px] text-[#9AA8BE]">
          Code expires in 10 minutes
        </p>

        <CustomButton
          variant="primary"
          fullWidth
          onClick={handleVerifyOtp}
          disabled={isLoading || otp.length !== OTP_LENGTH}
          className="!h-[50px] !rounded-xl !border-none !bg-[#1562EE] !text-[15px] !font-[Gilroy-Medium] hover:!bg-[#1254CC] disabled:!opacity-60"
        >
          {isLoading ? <Loader /> : "Verify & Sign in"}
        </CustomButton>

        <p className="text-center font-[Gilroy-Medium] text-[14px] text-[#6B7C99]">
          Didn't receive the code?{" "}
          {resendTimer > 0 ? (
            <span className="text-[#9AA8BE]">
              Resend in {resendTimer}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isLoading}
              className="font-[Gilroy-Bold] text-[#1562EE] transition-colors hover:text-[#0F4FBF] disabled:opacity-50"
            >
              Resend code
            </button>
          )}
        </p>
      </div>
    </div>
  );
};

export default OtpForm;
